import { Shell } from '../Shell';
import { useNav } from '../nav';
import { useAsync } from '../../core/useAsync';
import { cachedLeague, getMe, getProposals, getRoster } from '../../core/api';
import { avatarStyle, initials } from '../format';
import type { LeagueInfo, Me, Proposal, RosterMember } from '../../core/types';
import s from './Members.module.css';

// Who's in the league — visible to everyone, not just admins. Shows each active
// player (photo or initials). In a Jukebox league it also shows how many rounds each
// has proposed, so people can see the season filling up.
export function Members() {
  const nav = useNav();
  const roster = useAsync<RosterMember[]>(getRoster, []);
  const me = useAsync<Me>(getMe, []);
  const league = useAsync<LeagueInfo>(cachedLeague, []);
  const isJukebox = league.data?.league_type === 'jukebox';
  const proposals = useAsync<Proposal[]>(() => (isJukebox ? getProposals() : Promise.resolve([])), [isJukebox]);

  const myId = me.data?.user?.user_id ?? null;
  const members = [...(roster.data ?? [])].sort((a, b) => a.display_name.localeCompare(b.display_name));
  const countFor = (uid: number) => (proposals.data ?? []).filter((p) => p.proposed_by === uid).length;
  const totalProposed = proposals.data?.length ?? 0;

  return (
    <Shell>
      <div className={s.appbar}>
        <span className={s.ctx}>{league.data?.name ?? 'Members'}</span>
        <button className={s.menu} aria-label="Back" onClick={() => nav?.go('home')}>⋯</button>
      </div>
      <div className={s.head}>
        <h2>Members</h2>
        <p>{members.length} player{members.length === 1 ? '' : 's'}{isJukebox ? ` · ${totalProposed} round${totalProposed === 1 ? '' : 's'} proposed so far` : ''}</p>
      </div>

      {roster.loading ? (
        <div className={s.state}>Loading…</div>
      ) : members.length === 0 ? (
        <div className={s.state}>No players yet — share the invite code to get people in.</div>
      ) : (
        <div className={s.list}>
          {members.map((m) => {
            const you = m.user_id === myId;
            return (
              <div key={m.user_id} className={`${s.row}${you ? ' ' + s.you : ''}`}>
                <span className={s.av} style={avatarStyle(m.avatar_url, m.user_id)}>{m.avatar_url ? '' : initials(m.display_name)}</span>
                <span className={s.nm}>{m.display_name}{you ? <em className={s.youtag}>you</em> : null}</span>
                {isJukebox ? (
                  <span className={s.cnt} title="rounds proposed">{proposals.loading ? '…' : countFor(m.user_id)}<small>proposed</small></span>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </Shell>
  );
}
